import { createHttpClient } from '@adapters/HttpClient';
import { toast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ClientsApiAdapter,
  CreateClientPayload,
} from '../adapters/ClientsApiAdapter';

interface ValidationErrors {
  [key: string]: string[];
}

/**
 * Extrae el mensaje de error de la respuesta de la API.
 * La API puede devolver: { message: string, errors?: Record<string, string[]> }
 * El interceptor devuelve FormattedError con message y response (axios response).
 */
function getApiErrorMessage(error: any): string {
  const data = error?.response?.data ?? error?.data;
  if (data?.message && typeof data.message === 'string') {
    return data.message;
  }
  if (error?.message && typeof error.message === 'string' && error.message !== 'Error inesperado') {
    return error.message;
  }
  if (data?.error?.message && typeof data.error.message === 'string') {
    return data.error.message;
  }
  if (data?.error && typeof data.error === 'string') {
    return data.error;
  }
  if (error?.message) {
    return String(error.message);
  }
  return 'Error al crear el cliente';
}

// Para este flujo solo queremos mostrar el mensaje principal de la API en el toast,
// sin concatenar los errores por campo para evitar textos repetidos.
function buildToastDescription(message: string): string {
  return message;
}

export const useCreateClient = (
  onValidationError?: (errors: ValidationErrors) => void
) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new ClientsApiAdapter(httpClient);

  const createMutation = useMutation({
    mutationFn: (payload: CreateClientPayload) =>
      apiAdapter.createClient(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast({
        variant: 'success',
        title: 'cliente creado',
        description: 'El cliente se ha creado correctamente',
      });
      navigate('/clients');
    },
    onError: (error: any) => {
      // Mensaje principal desde la API (ej: "El campo vat ya ha sido registrado. (y 1 error más)")
      const errorMessage = getApiErrorMessage(error);
      // Errores por campo para el callback y para el toast
      const validationErrors: Record<string, string[]> | undefined =
        error?.response?.data?.errors ?? error?.data?.errors;

      if (validationErrors && onValidationError) {
        onValidationError(validationErrors);
      }

      const description = buildToastDescription(errorMessage);

      toast({
        variant: 'destructive',
        title: 'Error',
        description,
      });
    },
  });

  return {
    createClient: createMutation.mutate,
    isCreating: createMutation.isPending,
    error: createMutation.error,
  };
};
